import { type ChangeEvent, type RefObject, useRef } from 'react';
import {
  Button,
  Text,
  Box,
  Stack,
  IconCritical,
  Inline,
  TextLinkButton,
} from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import type { TranslationKeys } from '@vocab/core';

import { ACCEPTED_ADD_CANDIDATE_FILE_FORMAT } from '../../../utils/fileValidator';
import { attachmentDownloadUrl } from '../../../utils/attachmentUrls';
import * as styles from './UploadCandidateAttachment.css';
import type { CandidateAttachment } from './types';
import type { AttachmentType } from '../../../constants/attachmentTypes';
import {
  trackAddAttachmentClickedEvent,
  trackRemoveAttachmentClickedEvent,
} from '../../../utils/events/addCandidateEvents';
import TextButton from 'src/components/common/TextButton';
import translations from './.vocab/';
import downloadFile from 'src/DocumentViewer/utils/downloadFile';

type AddDocumentTranslationKeys = TranslationKeys<typeof translations>;

const labelToKeyMap: Record<string, AddDocumentTranslationKeys> = {
  Resumé: 'Add Resumé',
  'Cover Letter': 'Add Cover Letter',
};

interface Props {
  setAttachment: (attachment: CandidateAttachment) => void;
  attachment: CandidateAttachment;
  attachmentType: AttachmentType;
  fileFieldName: string;
}

const UploadCandidateAttachment = ({
  attachment,
  setAttachment,
  attachmentType,
  fileFieldName,
}: Props) => {
  const { t } = useTranslations(translations);

  const translationKey = labelToKeyMap[attachmentType.displayName];
  const ref: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const onClickAdd = () => {
    if (ref.current !== null) {
      ref.current.click();
      trackAddAttachmentClickedEvent({
        documentType: attachmentType.typeName,
      });
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setAttachment({
        ...attachment,
        file: event.target.files[0],
        isValid: true,
      } as CandidateAttachment);
    }
    event.target.value = '';
  };

  const removeFile = () => {
    setAttachment({ previousAttachment: null, file: null, isValid: true });
    if (ref.current !== null) {
      ref.current.value = '';
    }
    trackRemoveAttachmentClickedEvent({
      documentType: attachmentType.typeName,
    });
  };
  const buttonSection = (
    <div className={styles.addAttachmentButton}>
      <Button variant="ghost" tone="neutral" onClick={onClickAdd}>
        {t(translationKey)}
      </Button>
      <input
        data-testid="file-input"
        name={fileFieldName}
        type="file"
        style={{ display: 'none' }}
        ref={ref}
        onChange={onFileChange}
        accept={ACCEPTED_ADD_CANDIDATE_FILE_FORMAT.join(',')}
        value=""
      />
    </div>
  );
  const previousAttachment = attachment.previousAttachment;
  const previousAttachmentName = previousAttachment && (
    <TextLinkButton
      onClick={() => {
        downloadFile(
          attachmentDownloadUrl(
            previousAttachment.prospectId,
            previousAttachment.attachmentId,
          ),
          () => {},
        );
      }}
    >
      {previousAttachment.fileName}
    </TextLinkButton>
  );
  const fileName = attachment.file?.name;
  const selectedFileSection = (
    <Box display="flex" justifyContent="spaceBetween" alignItems="flexEnd">
      <Box>
        <Text>{previousAttachment ? previousAttachmentName : fileName}</Text>
      </Box>
      <Box />
      <Text>
        <TextButton onClick={removeFile}>{t('Remove')}</TextButton>
      </Text>
    </Box>
  );

  return (
    <Stack space="small">
      {fileName || previousAttachment ? selectedFileSection : buttonSection}
      {!attachment.isValid && (
        <Text tone="critical" size="small">
          <Inline space="xsmall">
            <IconCritical />
            <span>{t('Invalid attachment error message')}</span>
          </Inline>
        </Text>
      )}
    </Stack>
  );
};

export default UploadCandidateAttachment;
